import {useRef, useEffect, useState, useCallback, useMemo} from 'react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import safeStorage from '@github-ui/safe-storage'
import styles from './ExpandableContent.module.css'

const DEFAULT_MAX_HEIGHT = 256
const storage = safeStorage('localStorage')

interface ExpandableContentProps {
  /** Content rendered inside the expandable wrapper */
  children: React.ReactNode
  /** Additional class name applied to the outer wrapper */
  className?: string
  /** Section name to add to show more label for a11y */
  sectionLabel: string
  /** Max height in pixels before content is collapsed (default: 256) */
  maxHeight?: number
  /** localStorage key to persist expand/collapse state. When omitted, state is not persisted. */
  storageKey?: string
  /** Initial expanded state used on first view when no persisted preference exists (default: false) */
  defaultExpanded?: boolean
}

/** Wraps content with overflow detection, gradient fade, and "Show more" toggle */
export function ExpandableContent({
  children,
  sectionLabel,
  className,
  maxHeight = DEFAULT_MAX_HEIGHT,
  storageKey,
  defaultExpanded = false,
}: ExpandableContentProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)
  const [userPreference, setUserPreference] = useState<boolean | null>(null)

  const isExpanded = useMemo(() => {
    if (userPreference !== null) return userPreference
    if (storageKey) {
      const stored = storage.getItem(storageKey)
      if (stored !== null) return stored === 'true'
    }
    return defaultExpanded
  }, [userPreference, storageKey, defaultExpanded])

  useEffect(() => {
    const node = bodyRef.current
    if (!node) return

    const update = () => setContentHeight(node.scrollHeight)
    update()

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const node = bodyRef.current
    if (!node) return
    const id = requestAnimationFrame(() => setContentHeight(node.scrollHeight))
    return () => cancelAnimationFrame(id)
  }, [isExpanded])

  const handleToggle = useCallback(() => {
    const next = !isExpanded
    setUserPreference(next)
    if (storageKey) {
      storage.setItem(storageKey, JSON.stringify(next))
    }
  }, [isExpanded, storageKey])

  const isOverflowing = contentHeight > maxHeight
  const isCollapsed = isOverflowing && !isExpanded
  const expandedText = isExpanded ? 'Show less' : 'Show more'
  const appliedMaxHeight = isCollapsed ? maxHeight : Math.max(contentHeight, maxHeight)

  return (
    <div
      ref={bodyRef}
      className={clsx(
        styles.body,
        isCollapsed && styles.bodyCollapsed,
        isOverflowing && styles.bodyOverflowing,
        className,
      )}
      style={{maxHeight: `${appliedMaxHeight}px`}}
      data-testid="expandable-content-body"
    >
      <div className={styles.content} inert={isCollapsed ? true : undefined} data-testid="expandable-content-inner">
        {children}
      </div>
      {isOverflowing && (
        <div className={styles.toggle}>
          <Button
            variant="invisible"
            size="medium"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            aria-label={`${expandedText} ${sectionLabel}`}
          >
            {expandedText}
          </Button>
        </div>
      )}
    </div>
  )
}
